import React from 'react';
import {
  makeStyles,
  shorthands,
  Button,
  Text,
  Card,
} from '@fluentui/react-components';
import {
  ErrorCircleRegular,
  SignOutRegular,
} from '@fluentui/react-icons';
import { useMSALAuth } from '../../contexts/MSALAuthContext';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    backgroundColor: '#f3f2f1',
    ...shorthands.padding('24px'),
  },
  card: {
    maxWidth: '480px',
    width: '100%',
    ...shorthands.padding('32px'),
    textAlign: 'center',
  },
  icon: {
    fontSize: '48px',
    color: '#d13438',
    ...shorthands.margin('0', '0', '16px', '0'),
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
    ...shorthands.margin('0', '0', '12px', '0'),
  },
  message: {
    fontSize: '16px',
    color: '#605e5c',
    lineHeight: '22px',
    ...shorthands.margin('0', '0', '8px', '0'),
  },
  details: {
    fontSize: '14px',
    color: '#8a8886',
    lineHeight: '20px',
    ...shorthands.margin('0', '0', '24px', '0'),
  },
  actions: {
    display: 'flex',
    ...shorthands.gap('12px'),
    justifyContent: 'center',
  },
  button: {
    minWidth: '120px',
  },
});

interface AuthErrorProps {
  type: 'PERMISSION_DENIED' | 'AUTH_FAILED' | 'NETWORK_ERROR' | 'BACKEND_UNAVAILABLE';
  message?: string;
  details?: string;
}

export const AuthError: React.FC<AuthErrorProps> = ({ type, message, details }) => {
  const styles = useStyles();
  const { logout, clearError, clearAuthCache } = useMSALAuth();

  const getTitle = () => {
    switch (type) {
      case 'PERMISSION_DENIED':
        return 'Access Denied';
      case 'AUTH_FAILED':
        return 'Authentication Failed';
      case 'NETWORK_ERROR':
        return 'Connection Error';
      case 'BACKEND_UNAVAILABLE':
        return 'Backend Server Not Running';
      default:
        return 'Error';
    }
  };

  const handleSignOut = () => {
    clearError();
    logout();
  };

  const handleTryAgain = () => {
    clearError();
    // This will trigger a re-authentication attempt
    window.location.reload();
  };

  const handleClearCache = () => {
    clearAuthCache();
    // Give user feedback and reload after clearing cache
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  const isTokenCacheError = details?.includes('no_token_request_cache_error') || message?.includes('Token cache error');

  return (
    <div className={styles.container} data-testid="auth-error-page">
      <Card className={styles.card} data-testid="auth-error-card">
        <ErrorCircleRegular className={styles.icon} data-testid="auth-error-icon" />
        
        <Text className={styles.title} data-testid="auth-error-title">
          {getTitle()}
        </Text>
        
        {message && (
          <Text className={styles.message} data-testid="auth-error-message">
            {message}
          </Text>
        )}
        
        {details && (
          <Text className={styles.details} data-testid="auth-error-details">
            {details}
          </Text>
        )}
        
        {type === 'BACKEND_UNAVAILABLE' && (
          <Text className={styles.details} style={{ marginTop: '16px' }} data-testid="auth-error-backend-instructions">
            Please start the backend server on port 3000 by running:<br data-testid="auth-error-backend-command-break"/>
            <code style={{ fontFamily: 'monospace', backgroundColor: '#f3f2f1', padding: '4px 8px', borderRadius: '4px' }} data-testid="auth-error-backend-command">
              cd backend-functions && npm start
            </code>
          </Text>
        )}

        <div className={styles.actions} data-testid="auth-error-actions">
          <Button
            appearance="primary"
            icon={<SignOutRegular />}
            onClick={handleSignOut}
            className={styles.button}
            data-testid="auth-error-sign-out-button"
          >
            Sign Out
          </Button>
          
          {isTokenCacheError && (
            <Button
              appearance="secondary"
              onClick={handleClearCache}
              className={styles.button}
              data-testid="auth-error-clear-cache-button"
            >
              Clear Cache
            </Button>
          )}
          
          {type !== 'PERMISSION_DENIED' && (
            <Button
              appearance="outline"
              onClick={handleTryAgain}
              className={styles.button}
              data-testid="auth-error-try-again-button"
            >
              Try Again
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};